import type { PillTone } from '@/components/common/StatusPill';
import type { TimelineStep } from '@/components/common/Timeline';
import type { ReturnEntry } from '@/types';
import type { ReturnCase, ReturnReason, ReturnResolution, ReturnStatus } from '@/types/returns';
import { formatEventTime } from './shipment';

export const returnFormPath = (orderId: string) => `/customer/orders/${orderId}/return`;
export const returnStatusPath = (orderId: string) => `/customer/orders/${orderId}/return/status`;

/** Shots every return needs (Figma R2). */
export const REQUIRED_PHOTOS = ['Front', 'Back', 'Tags & box'];

export const RETURN_REASONS: { value: ReturnReason; title: string; description?: string }[] = [
  { value: 'changed_mind', title: 'Changed my mind' },
  { value: 'damaged', title: 'Damaged or defective', description: 'Show the issue clearly in your photos' },
  { value: 'different', title: 'Different from what I ordered' },
  { value: 'quality', title: 'Quality not as expected' },
];

export const reasonTitle = (reason?: ReturnReason) => RETURN_REASONS.find((r) => r.value === reason)?.title ?? '';

/** Step 4's name, which depends on the resolution picked in step 3. */
export const RESOLUTION_STEP: Record<ReturnResolution, string> = {
  replacement: 'Replacement',
  store_credit: 'Store credit',
  refund: 'Refund',
};

export const RETURN_STATUS_BADGE: Record<ReturnStatus, { tone: PillTone; label: string }> = {
  awaiting_approval: { tone: 'info', label: 'Awaiting approval' },
  in_progress: { tone: 'info', label: 'In progress' },
  pickup_failed: { tone: 'danger', label: 'Pickup failed' },
  completed: { tone: 'success', label: 'Completed' },
  rejected: { tone: 'danger', label: 'Rejected' },
  cancelled: { tone: 'neutral', label: 'Cancelled' },
};

/** "RET-58302" from a return ID. */
export const returnLabel = (id: string) => (id.toUpperCase().startsWith('RET') ? id.toUpperCase() : `RET-${id.slice(-6).toUpperCase()}`);

interface StepInput {
  requestedAt?: string;
  merchant?: string;
  resolution?: ReturnResolution;
  /** "Tue, 03 Nov · 10 AM – 2 PM". */
  pickup?: string;
  /** "Classic Cabin · Grey". */
  replacement?: string;
  /** "₹6,999". */
  amount?: string;
  refundTo?: string;
}

/** The standard steps of a return that's waiting for the store's approval (R7A–R7C). */
export function awaitingApprovalSteps({ requestedAt, merchant = 'The store', resolution, pickup, replacement, amount, refundTo }: StepInput): TimelineStep[] {
  const steps: TimelineStep[] = [
    { title: 'Return requested', meta: formatEventTime(requestedAt, { withYear: true }), state: 'done' },
    { title: `Approval by ${merchant}`, meta: `${merchant} is reviewing your request`, state: 'current' },
    { title: 'Pickup', meta: pickup, state: 'pending' },
    { title: 'Quality check', meta: `At the ${merchant} warehouse`, state: 'pending' },
  ];
  if (resolution === 'replacement') {
    steps.push({ title: 'Replacement shipped', meta: replacement, state: 'pending' }, { title: 'Replacement delivered', state: 'pending' });
  } else if (resolution === 'store_credit') {
    steps.push({ title: 'Store credit added', meta: amount && `${amount} to your ${merchant} account`, state: 'pending' });
  } else if (resolution === 'refund') {
    steps.push(
      { title: 'Refund initiated', meta: [amount && `${amount} to`, refundTo].filter(Boolean).join(' ') || undefined, state: 'pending' },
      { title: 'Refund credited', meta: 'Within 5–7 working days', state: 'pending' },
    );
  }
  return steps;
}

// Backend timeline statuses → customer wording (same mapping as the original TrackStatus page).
const EVENT_TITLES: Record<string, string> = {
  'Return Request Created': 'Return requested',
  requested: 'Return requested',
  approved: 'Approved',
  'Pickup Scheduled': 'Pickup scheduled',
  completed: 'Picked up',
  shipped: 'On its way to the warehouse',
  delivered: 'Reached the warehouse',
  failed: 'Pickup attempted',
  trigger_reorder: 'Replacement shipped',
  trigger_refund: 'Refund initiated',
  closed: 'Return completed',
};

function statusOf(entry: ReturnEntry): ReturnStatus {
  const status = String(entry.status ?? '').toLowerCase();
  const pickup = String(entry.courier_metadata?.pickup_status ?? '').toLowerCase();
  if (/cancel/.test(status)) return 'cancelled';
  if (/reject|denied/.test(status)) return 'rejected';
  if (/closed|complete|resolved/.test(status)) return 'completed';
  if (pickup === 'failed' || status === 'failed') return 'pickup_failed';
  if (/request|pending|review|processing/.test(status)) return 'awaiting_approval';
  return 'in_progress';
}

const resolutionOf = (entry: ReturnEntry & { selected_solution?: string }): ReturnResolution | undefined => {
  const type = String(entry.return_type ?? entry.selected_solution ?? '').toLowerCase();
  if (/replace|reorder|exchange/.test(type)) return 'replacement';
  if (/credit/.test(type)) return 'store_credit';
  if (/refund/.test(type)) return 'refund';
  return undefined;
};

/**
 * Status page view of a return from `get-return-onboarding-data`. The API has no customer-facing
 * step list, so this shows the return's own timeline events, with the latest marked current.
 */
export function buildReturnCase(entry: ReturnEntry, productTitle?: string): ReturnCase {
  const status = statusOf(entry);
  const resolution = resolutionOf(entry);
  const seen = new Set<string>();
  const events = [...(entry.timeline ?? [])]
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    .map((ev) => ({ ...ev, status: String(ev.status).replace(/^Shipment Status: /, '') }))
    .filter((ev) => (seen.has(ev.status) ? false : (seen.add(ev.status), true)));

  const steps: TimelineStep[] = events.length
    ? events.map((ev, i) => ({
        title: EVENT_TITLES[ev.status] ?? ev.status.charAt(0).toUpperCase() + ev.status.slice(1),
        meta: formatEventTime(ev.timestamp, { withYear: true }),
        state: ev.status === 'failed' ? 'failed' : i === events.length - 1 && status !== 'completed' ? 'current' : 'done',
      }))
    : [{ title: 'Return requested', meta: formatEventTime(entry.created_at, { withYear: true }), state: status === 'awaiting_approval' ? 'current' : 'done' }];

  return {
    id: entry.id,
    label: returnLabel(entry.id),
    productTitle,
    resolution,
    resolutionSummary: resolution ? RESOLUTION_STEP[resolution] : undefined,
    status,
    steps,
    pickupFailure: status === 'pickup_failed' ? 'The courier couldn’t collect the item. Pick a new slot to continue your return.' : undefined,
  };
}
